import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSiteSettings } from '@/settings/useSiteSettings';
import { listPublishedProducts } from '@/services/products';
import { listPublicPlans, formatPrice } from '@/services/plans';
import { testimonials } from '@/services/testimonials';
import type { Product } from '@/domain/types';
import type { ProductPlan } from '@/domain/types';

const storeFeatures = [
  {
    icon: 'ri-shield-check-line',
    title: 'Secure License System',
    desc: 'Every product comes with a cryptographically secure license key. Track activations, manage sites, and protect your intellectual property.',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Automatic Updates',
    desc: 'Updates delivered directly through WordPress. No manual downloads — your plugins stay current with one click.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Priority Support',
    desc: 'Fast, expert support directly from the developers. Average response time under 4 hours.',
  },
  {
    icon: 'ri-download-cloud-line',
    title: 'Instant Downloads',
    desc: 'Download your products immediately after purchase from your account dashboard. Always available, always versioned.',
  },
  {
    icon: 'ri-code-box-line',
    title: 'Developer Friendly',
    desc: 'Clean, documented code with hooks and filters. Follows WordPress coding standards with extensive documentation.',
  },
  {
    icon: 'ri-global-line',
    title: 'Multi-Site Ready',
    desc: 'Agency plans support multiple activations. Manage all client sites from a single dashboard.',
  },
];

function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-surface-200 animate-pulse">
      <div className="aspect-[16/10] bg-surface-200" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-surface-200 rounded w-3/4" />
        <div className="h-3 bg-surface-200 rounded w-full" />
        <div className="h-3 bg-surface-200 rounded w-2/3" />
        <div className="flex justify-between pt-1">
          <div className="h-5 bg-surface-200 rounded w-16" />
          <div className="h-4 bg-surface-200 rounded w-20" />
        </div>
      </div>
    </div>
  );
}

function ProductCard({ product }: { product: Product }) {
  const navigate = useNavigate();
  const typeColors: Record<string, string> = {
    plugin: 'bg-brand-50 text-brand-700',
    theme: 'bg-amber-50 text-amber-700',
    template: 'bg-sky-50 text-sky-700',
    addon: 'bg-violet-50 text-violet-700',
    asset: 'bg-rose-50 text-rose-700',
    app: 'bg-teal-50 text-teal-700',
  };
  return (
    <div
      onClick={() => navigate(`/product/${product.slug}`)}
      className="group bg-white rounded-xl overflow-hidden border border-surface-200 hover:border-brand-300 hover:shadow-lg transition-all duration-300 cursor-pointer">
      <div className="relative overflow-hidden aspect-[16/10]">
        {product.thumbnail ? (
          <img
            src={product.thumbnail}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-brand-50 to-surface-100 flex items-center justify-center">
            <i className="ri-box-3-line text-4xl text-brand-300" />
          </div>
        )}
        <span
          className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${typeColors[product.type] || 'bg-surface-100 text-surface-600'}`}>
          {product.type}
        </span>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="font-semibold text-surface-900 text-base mb-1 group-hover:text-brand-600 transition-colors">
          {product.name}
        </h3>
        <p className="text-sm text-surface-500 leading-relaxed mb-3 line-clamp-2">
          {product.shortDescription}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {product.isFree ? (
              <span className="text-lg font-bold text-brand-600">Free</span>
            ) : (
              <>
                <span className="text-lg font-bold text-surface-900">
                  ${product.priceFrom}
                </span>
                <span className="text-xs text-surface-400">from</span>
              </>
            )}
          </div>
          <span className="text-sm font-medium text-brand-600 group-hover:text-brand-700 flex items-center gap-1">
            View Details <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </div>
  );
}

function PricingCard({ plan }: { plan: ProductPlan }) {
  const navigate = useNavigate();
  const sitesLabel =
    plan.siteLimit >= 999
      ? 'Unlimited sites'
      : `${plan.siteLimit} site${plan.siteLimit !== 1 ? 's' : ''}`;
  return (
    <div
      className={`relative rounded-2xl p-6 md:p-8 border transition-all duration-300 hover:shadow-lg flex flex-col ${plan.isPopular ? 'bg-surface-900 text-white border-surface-900 scale-[1.02] md:scale-[1.04]' : 'bg-white border-surface-200 hover:border-brand-300'}`}>
      {plan.isPopular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand-500 text-white text-xs font-semibold">
          Most Popular
        </span>
      )}
      <h3
        className={`text-lg font-semibold mb-1 ${plan.isPopular ? 'text-white' : 'text-surface-900'}`}>
        {plan.name}
      </h3>
      <div className="flex items-baseline gap-1 mb-4">
        <span
          className={`text-3xl md:text-4xl font-bold ${plan.isPopular ? 'text-white' : 'text-surface-900'}`}>
          ${plan.price}
        </span>
        <span
          className={`text-sm ${plan.isPopular ? 'text-surface-400' : 'text-surface-500'}`}>
          {plan.billingType === 'one_time'
            ? 'one-time'
            : `/${plan.intervalUnit}`}
        </span>
      </div>
      <ul className="space-y-3 mb-6 flex-1">
        <li className="flex items-start gap-2 text-sm">
          <i
            className={`ri-check-line mt-0.5 shrink-0 ${plan.isPopular ? 'text-brand-400' : 'text-brand-600'}`}
          />
          <span
            className={
              plan.isPopular ? 'text-surface-300' : 'text-surface-600'
            }>
            {sitesLabel}
          </span>
        </li>
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <i
              className={`ri-check-line mt-0.5 shrink-0 ${plan.isPopular ? 'text-brand-400' : 'text-brand-600'}`}
            />
            <span
              className={
                plan.isPopular ? 'text-surface-300' : 'text-surface-600'
              }>
              {f}
            </span>
          </li>
        ))}
      </ul>
      <button
        onClick={() => navigate('/products')}
        className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${plan.isPopular ? 'bg-brand-500 hover:bg-brand-400 text-white' : 'bg-surface-100 hover:bg-surface-200 text-surface-900'}`}>
        Get Started
      </button>
    </div>
  );
}

function PricingCardSkeleton({ popular = false }: { popular?: boolean }) {
  return (
    <div
      className={`rounded-2xl p-6 md:p-8 border animate-pulse ${popular ? 'bg-surface-800 border-surface-700 scale-[1.02]' : 'bg-white border-surface-200'}`}>
      <div
        className={`h-5 rounded w-1/2 mb-4 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`}
      />
      <div
        className={`h-10 rounded w-1/3 mb-6 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`}
      />
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={`h-3 rounded mb-3 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`}
          style={{ width: `${60 + i * 8}%` }}
        />
      ))}
      <div
        className={`h-10 rounded-lg mt-6 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`}
      />
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [products, setProducts] = useState<Product[]>([]);
  const [plans, setPlans] = useState<ProductPlan[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    listPublishedProducts(4)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoadingProducts(false));
    listPublicPlans()
      .then((all) => setPlans(all.slice(0, 3)))
      .catch(() => setPlans([]))
      .finally(() => setLoadingPlans(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[600px] md:min-h-[720px] flex items-center overflow-hidden bg-surface-950">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-surface-950 via-surface-900 to-brand-950" />
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 50%, #16a34a 0%, transparent 50%), radial-gradient(circle at 80% 20%, #0ea5e9 0%, transparent 40%)',
            }}
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 py-24 md:py-32">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-500/15 border border-brand-500/20 text-brand-400 text-xs font-medium mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />{' '}
              Premium WordPress Plugins & Tools
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
              Build Faster with
              <br />
              <span className="text-brand-400">Professional Tools</span>
            </h1>
            <p className="text-base md:text-lg text-surface-300 max-w-xl mx-auto mb-8 leading-relaxed">
              {settings.footer.description ||
                'Premium Elementor plugins and WordPress tools crafted for agencies and developers. Secure licensing, automatic updates, and expert support included.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/products')}
                className="px-6 py-3 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-semibold text-sm transition-colors whitespace-nowrap">
                Browse Products
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-6 py-3 rounded-lg border border-surface-600 hover:border-surface-500 text-surface-200 font-medium text-sm transition-colors whitespace-nowrap">
                Create Free Account
              </button>
            </div>
            <div className="mt-10 flex items-center justify-center gap-6 md:gap-8 text-sm text-surface-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-download-line text-brand-400" />
                {loadingProducts ? '…' : `${products.length}+ products`}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-brand-400" /> Secure
                Licensing
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-brand-400" /> Auto Updates
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-surface-900 mb-3">
              Built for Professionals
            </h2>
            <p className="text-surface-500 max-w-lg mx-auto text-sm md:text-base">
              Every product is crafted with performance, security, and ease of
              use in mind.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {storeFeatures.map((f) => (
              <div
                key={f.title}
                className="group p-5 md:p-6 rounded-xl border border-surface-200 hover:border-brand-300 hover:shadow-md transition-all duration-300 bg-surface-50/50">
                <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4 group-hover:bg-brand-100 transition-colors">
                  <i className={`${f.icon} text-lg text-brand-600`} />
                </div>
                <h3 className="font-semibold text-surface-900 mb-2 text-base">
                  {f.title}
                </h3>
                <p className="text-sm text-surface-500 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-surface-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-10">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold text-surface-900 mb-2">
                Featured Products
              </h2>
              <p className="text-surface-500 text-sm md:text-base">
                Our latest tools, built for modern WordPress development.
              </p>
            </div>
            <Link
              to="/products"
              className="text-sm font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1 shrink-0">
              View All <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {loadingProducts ? (
              Array.from({ length: 4 }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))
            ) : products.length > 0 ? (
              products.map((p) => <ProductCard key={p.id} product={p} />)
            ) : (
              <div className="col-span-4 py-16 text-center text-surface-400 text-sm">
                No products published yet.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-surface-900 mb-3">
              Simple, Transparent Pricing
            </h2>
            <p className="text-surface-500 max-w-lg mx-auto text-sm md:text-base">
              No hidden fees. Pick the plan that fits your workflow.
            </p>
          </div>
          {loadingPlans ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
              <PricingCardSkeleton />
              <PricingCardSkeleton popular />
              <PricingCardSkeleton />
            </div>
          ) : plans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
              {plans.map((plan) => (
                <PricingCard key={plan.id} plan={plan} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-surface-400 text-sm">
              No plans available yet.
            </div>
          )}
          <p className="text-center text-xs text-surface-400 mt-8">
            All plans include a 14-day money-back guarantee.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-surface-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold text-surface-900 mb-4">
                Get Started in Minutes
              </h2>
              <p className="text-surface-500 text-sm md:text-base mb-8 leading-relaxed">
                From purchase to live site in three simple steps. No complex
                setup, no configuration files.
              </p>
              <div className="space-y-6">
                {[
                  {
                    step: '01',
                    title: 'Purchase & Download',
                    desc: 'Buy your license and instantly download the product from your dashboard. All files are versioned and verified.',
                  },
                  {
                    step: '02',
                    title: 'Install & Activate',
                    desc: 'Upload to WordPress and enter your license key under Settings to unlock all features and automatic updates.',
                  },
                  {
                    step: '03',
                    title: 'Build & Launch',
                    desc: 'Use the plugin inside Elementor, customize to your needs, and launch. Support is one message away.',
                  },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <span className="shrink-0 w-10 h-10 rounded-lg bg-brand-50 text-brand-600 font-bold text-sm flex items-center justify-center">
                      {item.step}
                    </span>
                    <div>
                      <h4 className="font-semibold text-surface-900 text-base mb-1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-surface-500 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-brand-50 to-surface-100 border border-surface-200 p-8 flex items-center justify-center min-h-[320px]">
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-brand-600 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-shield-keyhole-line text-4xl text-white" />
                </div>
                <p className="text-surface-700 font-semibold text-lg mb-2">
                  Licensed & Secure
                </p>
                <p className="text-surface-500 text-sm max-w-xs">
                  Every download is tied to your license key and delivered
                  securely through your dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-surface-900 mb-3">
              Loved by Builders
            </h2>
            <p className="text-surface-500 max-w-lg mx-auto text-sm md:text-base">
              What developers, agencies, and designers say about our products.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="p-5 md:p-6 rounded-xl border border-surface-200 bg-surface-50/50 hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-surface-900 text-sm">
                      {t.name}
                    </h4>
                    <p className="text-xs text-surface-500">{t.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`ri-star-fill text-xs ${i < t.rating ? 'text-amber-400' : 'text-surface-300'}`}
                    />
                  ))}
                </div>
                <p className="text-sm text-surface-600 leading-relaxed mb-3">
                  {t.content}
                </p>
                <span className="text-xs font-medium text-brand-600">
                  {t.product}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-surface-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Ready to build something great?
          </h2>
          <p className="text-surface-400 max-w-lg mx-auto mb-8 text-sm md:text-base leading-relaxed">
            Join agencies and developers who trust {settings.general.shortName}{' '}
            for their WordPress projects.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
            <button
              onClick={() => navigate('/products')}
              className="px-6 py-3 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-semibold text-sm transition-colors whitespace-nowrap">
              Browse Store
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-3 rounded-lg border border-surface-700 hover:border-surface-500 text-surface-200 font-medium text-sm transition-colors whitespace-nowrap">
              Create Account
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {[
              {
                icon: 'ri-mail-line',
                label: 'Email',
                value: settings.general.supportEmail,
              },
              {
                icon: 'ri-time-line',
                label: 'Response Time',
                value: settings.general.supportResponseTime || '< 4 hours avg',
              },
              {
                icon: 'ri-map-pin-line',
                label: 'Based in',
                value: 'Remote-first, Global',
              },
            ].map((item) => (
              <div key={item.label}>
                <i className={`${item.icon} text-brand-400 text-xl mb-2`} />
                <p className="text-xs text-surface-500 mb-1">{item.label}</p>
                <p className="text-sm text-surface-300 font-medium">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
